import React, { useCallback, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { CloseXComponent as CloseXIcon } from 'ui/Icons';

export interface iGeneralModalProps {
  isOpen?: boolean;
  children?: React.ReactNode;
  modalOverlayClassName?: string;
  modalWindowClassName?: string;
  isCloseButtonVisible?: boolean;
  shouldCloseByClickingOutside?: boolean;
  onClose?: () => void;
}

export const GeneralModal = (props: iGeneralModalProps) => {
  const {
    isOpen = true,
    children,
    modalOverlayClassName = '',
    modalWindowClassName = '',
    isCloseButtonVisible = true,
    shouldCloseByClickingOutside = false,
    onClose,
  } = props;

  const [isBrowser, setIsBrowser] = useState(false);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      // Store the current scroll position before locking
      scrollPositionRef.current = window.scrollY;

      // Apply fixed positioning with explicit top to maintain visual position
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollPositionRef.current}px`;
    } else {
      // Restore original body styling
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';

      // Restore scroll position
      window.scrollTo(0, scrollPositionRef.current);
    }

    return () => {
      // Cleanup if component unmounts while modal is open
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';

      if (isOpen) {
        window.scrollTo(0, scrollPositionRef.current);
      }
    };
  }, [isOpen]);

  const handleModalClose = useCallback(() => {
    onClose && onClose();
  }, [onClose]);

  const handleModalOverlayClick = useCallback(
    e => {
      if (shouldCloseByClickingOutside && e.target === e.currentTarget) {
        handleModalClose();
      }
    },
    [shouldCloseByClickingOutside, handleModalClose]
  );

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const modalContent = (
    <div
      className={classnames(
        'modal-overlay fixed z-[1200] bg-white md:bg-black-transparent flex justify-center items-center top-0 left-0 right-0 bottom-0 overflow-y-auto px-[20px] py-[40px]',
        modalOverlayClassName
      )}
      onClick={handleModalOverlayClick}
    >
      <div
        className={classnames(
          'modal-window bg-white shadow-pe5 min-h-[200px] md:min-h-70px relative max-h-screen overflow-y-auto',
          modalWindowClassName
        )}
        style={{
          boxShadow:
            '0px 0px 1px 0px rgba(15, 23, 42, 0.06), 0px 10px 15px -3px rgba(15, 23, 42, 0.1), 0px 4px 6px -2px rgba(15, 23, 42, 0.05',
        }}
      >
        {isCloseButtonVisible && (
          <div
            className="modal-close-button w-40px h-40px flex justify-center items-center absolute top-15px right-15px cursor-pointer"
            onClick={handleModalClose}
          >
            <CloseXIcon />
          </div>
        )}
        {children}
      </div>
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return isBrowser ? ReactDOM.createPortal(modalContent, document.getElementById('portal-modal') as HTMLElement) : null;
};
